import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useTechnologyStack } from '../hooks/useTechnologyStack'
import cdmData from '../../../shared/data/cyberDefenseMatrix.json'
import AddEditTechnologyModal from '../../../shared/components/AddEditTechnologyModal'
import TechnologyDetailModal from '../../../shared/components/TechnologyDetailModal'

// Color classes for CDM cells based on operational function color
const cellColors = {
  blue: {
    filled: 'bg-blue-100 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800',
    empty: 'bg-blue-50/50 dark:bg-blue-950/30 border-dashed border-blue-200 dark:border-blue-800/50',
    header: 'text-blue-700 dark:text-blue-300'
  },
  green: {
    filled: 'bg-green-100 dark:bg-green-900/40 border-green-200 dark:border-green-800',
    empty: 'bg-green-50/50 dark:bg-green-950/30 border-dashed border-green-200 dark:border-green-800/50',
    header: 'text-green-700 dark:text-green-300'
  },
  yellow: {
    filled: 'bg-yellow-100 dark:bg-yellow-900/40 border-yellow-200 dark:border-yellow-800',
    empty: 'bg-yellow-50/50 dark:bg-yellow-950/30 border-dashed border-yellow-200 dark:border-yellow-800/50',
    header: 'text-yellow-700 dark:text-yellow-300'
  },
  orange: {
    filled: 'bg-orange-100 dark:bg-orange-900/40 border-orange-200 dark:border-orange-800',
    empty: 'bg-orange-50/50 dark:bg-orange-950/30 border-dashed border-orange-200 dark:border-orange-800/50',
    header: 'text-orange-700 dark:text-orange-300'
  },
  purple: {
    filled: 'bg-purple-100 dark:bg-purple-900/40 border-purple-200 dark:border-purple-800',
    empty: 'bg-purple-50/50 dark:bg-purple-950/30 border-dashed border-purple-200 dark:border-purple-800/50',
    header: 'text-purple-700 dark:text-purple-300'
  }
}

function TechnologyStack({ clientId: propClientId, embedded = false, onNavigateBack }) {
  const params = useParams()
  const clientId = propClientId || params.clientId
  const { clients, loading: clientsLoading } = useAssessment()
  const {
    technologies, loading: techLoading, error,
    addTechnology, updateTechnology, deleteTechnology, buildCdmGrid
  } = useTechnologyStack(clientId)

  const [showModal, setShowModal] = useState(false)
  const [editingTech, setEditingTech] = useState(null)
  const [selectedCell, setSelectedCell] = useState(null)
  const [viewingTech, setViewingTech] = useState(null)

  const client = clients.find(c => c.id === clientId)
  const cdmGrid = buildCdmGrid()
  const filledCells = Object.keys(cdmGrid).length
  const gapCount = 25 - filledCells

  if (clientsLoading || techLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Client not found</p>
        <Link to="/" className="text-primary-600 hover:text-primary-700 dark:text-accent-300 mt-2 inline-block">
          Go home
        </Link>
      </div>
    )
  }

  const handleAddNew = (prefilledCell = null) => {
    setEditingTech(null)
    setSelectedCell(prefilledCell)
    setShowModal(true)
  }

  const handleView = (tech) => {
    setViewingTech(tech)
  }

  const handleEdit = (tech) => {
    setViewingTech(null)
    setEditingTech(tech)
    setSelectedCell(null)
    setShowModal(true)
  }

  const handleSave = async (techData) => {
    if (editingTech) {
      return await updateTechnology(editingTech.id, techData)
    } else {
      return await addTechnology(techData)
    }
  }

  const handleDelete = async (techId) => {
    return await deleteTechnology(techId)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className={`font-bold text-gray-900 dark:text-white ${embedded ? 'text-lg' : 'text-2xl'}`}>Security Tooling</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {technologies.length} tool{technologies.length !== 1 ? 's' : ''} mapped to the Cyber Defense Matrix
            {gapCount > 0 && (
              <span className="text-amber-600 dark:text-amber-400 ml-2">
                ({gapCount} coverage gap{gapCount !== 1 ? 's' : ''})
              </span>
            )}
          </p>
        </div>
        <button onClick={() => handleAddNew()} className="btn-primary text-sm">
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Technology
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* CDM Matrix */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cyber Defense Matrix</h3>
          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-sm bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 mr-1"></div>
              Covered
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-sm border border-dashed border-gray-300 dark:border-gray-600 mr-1"></div>
              Gap
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="grid grid-cols-6 gap-2 min-w-[640px]">
            {/* Header row */}
            <div></div>
            {cdmData.operationalFunctions.map(func => (
              <div
                key={func.id}
                className={`text-center text-sm font-semibold pb-2 ${cellColors[func.color]?.header || 'text-gray-700 dark:text-gray-300'}`}
              >
                {func.name}
              </div>
            ))}

            {/* Data rows */}
            {cdmData.assetClasses.map(asset => (
              <div key={asset.id} className="contents">
                <div className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300 pr-2">
                  {asset.name}
                </div>
                {cdmData.operationalFunctions.map(func => {
                  const key = `${asset.id}-${func.id}`
                  const tools = cdmGrid[key] || []
                  const hasTools = tools.length > 0
                  const colors = cellColors[func.color] || cellColors.blue

                  return (
                    <div
                      key={key}
                      onClick={() => {
                        if (!hasTools) {
                          handleAddNew({ assetClass: asset.id, function: func.id })
                        }
                      }}
                      className={`min-h-[80px] rounded-lg p-2 border transition-all ${
                        hasTools
                          ? colors.filled
                          : `${colors.empty} cursor-pointer hover:shadow-md`
                      }`}
                    >
                      {!hasTools ? (
                        <div className="flex items-center justify-center h-full text-xs text-gray-400 dark:text-gray-500">
                          + Add
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {tools.map(tool => (
                            <button
                              key={tool.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleView(tool)
                              }}
                              className="block w-full text-left text-xs px-1.5 py-1 rounded bg-white/60 dark:bg-gray-900/50 hover:bg-white dark:hover:bg-gray-900 text-gray-800 dark:text-gray-200 truncate transition-colors"
                              title={`${tool.name}${tool.vendor ? ` (${tool.vendor})` : ''}`}
                            >
                              {tool.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>

        <p className="text-[10px] text-gray-400 dark:text-gray-500 text-right mt-4">
          Framework: <a href="https://cyberdefensematrix.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 dark:hover:text-gray-300">Cyber Defense Matrix</a> by Sounil Yu, used under <a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 dark:hover:text-gray-300">CC BY-SA 3.0</a>
        </p>
      </div>

      {/* Technology List */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          All Technologies
          <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">({technologies.length})</span>
        </h3>

        {technologies.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 mb-2">No technologies added yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Click "Add Technology" or click any empty cell in the matrix above to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {technologies.map(tech => (
              <div
                key={tech.id}
                onClick={() => handleView(tech)}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">{tech.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {[tech.vendor, (tech.categories?.length ? tech.categories.join(', ') : tech.category)].filter(Boolean).join(' \u00b7 ') || 'Uncategorized'}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {tech.assetClasses.map(ac => (
                      <span
                        key={ac}
                        className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        {cdmData.assetClasses.find(a => a.id === ac)?.name || ac}
                      </span>
                    ))}
                    <span className="text-xs text-gray-300 dark:text-gray-500 px-1">/</span>
                    {tech.operationalFunctions.map(fn => (
                      <span
                        key={fn}
                        className="text-xs px-1.5 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-accent-200"
                      >
                        {cdmData.operationalFunctions.find(f => f.id === fn)?.name || fn}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(tech.id)
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Detail Modal */}
      {viewingTech && (
        <TechnologyDetailModal
          technology={viewingTech}
          onEdit={() => handleEdit(viewingTech)}
          onClose={() => setViewingTech(null)}
        />
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <AddEditTechnologyModal
          technology={editingTech}
          prefilledCell={selectedCell}
          onSave={handleSave}
          onDelete={editingTech ? () => handleDelete(editingTech.id) : null}
          onClose={() => {
            setShowModal(false)
            setEditingTech(null)
            setSelectedCell(null)
          }}
        />
      )}

    </div>
  )
}

export default TechnologyStack
